import { Building2, Edit3, Plus, Trash2, UserPlus, UsersRound } from "lucide-react";
import { FormEvent, useEffect, useState } from "react";
import { Badge, formatDate } from "../components/Badge";
import { Field, Input, Select } from "../components/Form";
import { Empty, ErrorState, Failure, Loading, Success } from "../components/State";
import { api, json } from "../lib/api";
import type { Venture, VentureMember } from "../lib/types";
import { useAsync } from "../lib/useAsync";

const blankVenture: Partial<Venture> = {
  kind: "unregistered_project",
  display_name: "",
  legal_name: "",
  unified_social_credit_code: "",
  registration_region_code: "",
  established_on: "",
};

export function VenturesPage() {
  const ventures = useAsync(() => api<{ items: Venture[] }>("/ventures"), []);
  const [selectedID, setSelectedID] = useState("");
  const [editing, setEditing] = useState<Partial<Venture>>();
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const selected = ventures.data?.items.find((item) => item.id === selectedID) ?? ventures.data?.items[0];

  useEffect(() => {
    if (!selectedID && ventures.data?.items[0]) setSelectedID(ventures.data.items[0].id);
  }, [selectedID, ventures.data?.items]);

  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (!editing) return;
    setBusy(true);
    setError("");
    try {
      const body = {
        kind: editing.kind,
        display_name: editing.display_name,
        legal_name: editing.legal_name || null,
        unified_social_credit_code: editing.unified_social_credit_code || null,
        registration_region_code: editing.registration_region_code || null,
        established_on: editing.established_on || null,
        version: editing.id ? editing.version : undefined,
      };
      const saved = editing.id
        ? await api<Venture>(`/ventures/${editing.id}`, { method: "PATCH", body: json(body) })
        : await api<Venture>("/ventures", { method: "POST", body: json(body) });
      setEditing(undefined);
      setSelectedID(saved.id);
      setMessage(editing.id ? "创业主体已更新" : "创业主体已创建");
      ventures.retry();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "保存创业主体失败");
    } finally {
      setBusy(false);
    }
  };

  return <div className="ventures-page page-shell">
    <header className="page-heading"><span>MY VENTURES</span><h1>创业主体</h1><p>管理项目、企业和协作成员；入驻资格会以活动成员为准。</p><button className="primary-button" onClick={() => setEditing({ ...blankVenture })}><Plus size={16} />新建主体</button></header>
    {message && <Success>{message}</Success>}
    {error && <Failure>{error}</Failure>}
    {ventures.loading ? <Loading label="正在加载创业主体" /> : ventures.error ? <ErrorState error={ventures.error} retry={ventures.retry} /> : !ventures.data?.items.length ? <Empty title="还没有创业主体" detail="先创建一个项目或企业，再发起入驻申请。" /> : <div className="ventures-layout">
      <aside className="venture-list">{ventures.data.items.map((item) => <button className={item.id === selected?.id ? "active" : ""} key={item.id} onClick={() => setSelectedID(item.id)}><span><Building2 size={18} /></span><div><strong>{item.display_name}</strong><small>{ventureKindName(item.kind)} · {roleName(item.member_role)}</small></div></button>)}</aside>
      {selected && <section className="venture-detail">
        <header><div><Badge tone={selected.status === "active" ? "success" : "neutral"}>{selected.status === "active" ? "有效" : selected.status}</Badge><h2>{selected.display_name}</h2><p>{selected.legal_name || ventureKindName(selected.kind)} · 更新于 {formatDate(selected.updated_at)}</p></div>{["owner", "admin"].includes(selected.member_role) && <button className="secondary-button" onClick={() => setEditing({ ...selected })}><Edit3 size={16} />编辑资料</button>}</header>
        <dl className="venture-facts"><div><dt>主体类型</dt><dd>{ventureKindName(selected.kind)}</dd></div><div><dt>信用代码</dt><dd>{selected.unified_social_credit_code || "-"}</dd></div><div><dt>注册地区</dt><dd>{selected.registration_region_code || "-"}</dd></div><div><dt>成立日期</dt><dd>{selected.established_on?.slice(0, 10) || "-"}</dd></div></dl>
        <VentureMembers venture={selected} />
      </section>}
    </div>}
    {editing && <div className="modal-layer"><button className="modal-backdrop" aria-label="关闭" onClick={() => setEditing(undefined)} /><form className="modal-card venture-editor" onSubmit={save}><header><h2>{editing.id ? "编辑创业主体" : "新建创业主体"}</h2><button type="button" onClick={() => setEditing(undefined)}>×</button></header><Field label="主体类型" required><Select value={editing.kind} onChange={(event) => setEditing({ ...editing, kind: event.target.value as Venture["kind"] })}><option value="unregistered_project">未注册项目</option><option value="company">企业</option><option value="sole_trader">个体工商户</option><option value="other">其他</option></Select></Field><Field label="展示名称" required><Input required minLength={2} maxLength={200} value={editing.display_name || ""} onChange={(event) => setEditing({ ...editing, display_name: event.target.value })} /></Field>{["company", "sole_trader"].includes(editing.kind || "") && <><Field label="法定名称" required><Input required maxLength={200} value={editing.legal_name || ""} onChange={(event) => setEditing({ ...editing, legal_name: event.target.value })} /></Field><Field label="统一社会信用代码" required><Input required pattern="[0-9A-HJ-NPQRTUWXY]{18}" maxLength={18} value={editing.unified_social_credit_code || ""} onChange={(event) => setEditing({ ...editing, unified_social_credit_code: event.target.value.toUpperCase() })} /></Field></>}<div className="form-grid cols-2"><Field label="注册地区代码"><Input pattern="[0-9]{6}" maxLength={6} value={editing.registration_region_code || ""} onChange={(event) => setEditing({ ...editing, registration_region_code: event.target.value.replace(/\D/g, "") })} /></Field><Field label="成立日期"><Input type="date" value={editing.established_on?.slice(0, 10) || ""} onChange={(event) => setEditing({ ...editing, established_on: event.target.value })} /></Field></div><button className="primary-button" disabled={busy}>{busy ? "保存中..." : "保存主体"}</button></form></div>}
  </div>;
}

function VentureMembers({ venture }: { venture: Venture }) {
  const members = useAsync(() => api<{ items: VentureMember[] }>(`/ventures/${venture.id}/members`), [venture.id]);
  const [inviting, setInviting] = useState(false);
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState<"admin" | "member">("member");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const canManage = venture.member_role === "owner" || venture.member_role === "admin";

  const invite = async (event: FormEvent) => {
    event.preventDefault();
    setBusy("invite");
    setError("");
    try {
      await api(`/ventures/${venture.id}/members`, { method: "POST", body: json({ phone, role }) });
      setPhone("");
      setInviting(false);
      members.retry();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "添加成员失败");
    } finally {
      setBusy("");
    }
  };

  const changeRole = async (member: VentureMember, nextRole: string) => {
    setBusy(member.id);
    setError("");
    try {
      await api(`/ventures/${venture.id}/members/${member.id}`, { method: "PATCH", body: json({ role: nextRole }) });
      members.retry();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "更新成员角色失败");
    } finally {
      setBusy("");
    }
  };

  const remove = async (member: VentureMember) => {
    if (!window.confirm(`确认移除成员“${member.display_name}”？`)) return;
    setBusy(member.id);
    setError("");
    try {
      await api(`/ventures/${venture.id}/members/${member.id}`, { method: "DELETE" });
      members.retry();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "移除成员失败");
    } finally {
      setBusy("");
    }
  };

  return <section className="venture-members"><header><div><span>VENTURE MEMBERS</span><h3><UsersRound size={18} />主体成员</h3></div>{canManage && <button className="secondary-button" onClick={() => setInviting(!inviting)}><UserPlus size={16} />添加成员</button>}</header>{error && <Failure>{error}</Failure>}{inviting && <form className="member-invite" onSubmit={invite}><Field label="成员注册手机号" required hint="仅用于精确匹配平台账号，保存后只显示脱敏号码"><Input required inputMode="numeric" pattern="1[3-9][0-9]{9}" maxLength={11} value={phone} onChange={(event) => setPhone(event.target.value.replace(/\D/g, ""))} /></Field><Field label="成员角色"><Select value={role} onChange={(event) => setRole(event.target.value as "admin" | "member")}><option value="member">普通成员</option><option value="admin">主体管理员</option></Select></Field><button className="primary-button" disabled={busy === "invite"}>{busy === "invite" ? "添加中..." : "确认添加"}</button></form>}{members.loading ? <Loading /> : members.error ? <ErrorState error={members.error} retry={members.retry} /> : <div className="member-list">{members.data?.items.map((member) => <article key={member.id}><span className="member-avatar">{member.display_name.slice(0, 1)}</span><div><strong>{member.display_name}</strong><small>{member.masked_phone || "已验证平台账号"} · 加入于 {formatDate(member.joined_at)}</small></div>{member.role === "owner" ? <Badge tone="accent">负责人</Badge> : canManage ? <><Select aria-label={`${member.display_name}的角色`} disabled={busy === member.id} value={member.role} onChange={(event) => void changeRole(member, event.target.value)}><option value="member">普通成员</option><option value="admin">主体管理员</option></Select><button className="icon-button danger" title="移除成员" disabled={busy === member.id} onClick={() => void remove(member)}><Trash2 size={16} /></button></> : <Badge>{roleName(member.role)}</Badge>}</article>)}</div>}</section>;
}

function ventureKindName(kind: Venture["kind"]) {
  return { unregistered_project: "未注册项目", sole_trader: "个体工商户", company: "企业", other: "其他主体" }[kind];
}

function roleName(role: Venture["member_role"] | VentureMember["role"]) {
  return { owner: "负责人", admin: "主体管理员", member: "普通成员" }[role];
}
